import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Dimmer, Loader } from "semantic-ui-react";
import {
  useGetCountries,
  useGetProduceTypes,
  useGetSpecifyIngredient
} from "../../../api/static-data";
import SupplierRegisterForm from "../../supplier/supplierRegisterForm/SuppllierRegisterForm";
import "./IngredientRegister.scss";
import SupplerIngredientDataView from "./SupplierIngredientDataView";
import { useSupplierDataType } from "./hooks/useSupplierDataType";
import { useSupplierFieldState } from "./hooks/useSupplierFieldState";
import { useSupplierRegisterForm } from "./hooks/useSupplierRegisterForm";
import { useSupplierSalesUnitSync } from "./hooks/useSupplierSalesUnitSync";
import { useSupplierSave } from "./hooks/useSupplierSave";
import { useSupplierSelection } from "./hooks/useSupplierSelection";
import {
  SupplierDataShape,
  SupplierIngredientDetails,
  SupplierRegisterProps
} from "./supplierRegister.types";
import { derivePackageFlags, getIngredientId, getSupplierId } from "./supplierRegister.utils";

const SupplerRegister = forwardRef(function SupplerRegister(
  props: SupplierRegisterProps,
  ref
) {
  const {
    ingredientSupplierList = [],
    supplierCurrentData,
    ingredientData,
    refetchSupplier = () => {},
    setIngredientData = () => {},
    setSupplier = () => {},
    loadPackageData = () => {},
    hideIngredientView = () => {},
    disableEdit = false,
    dataView = false,
    changeDataIngredient = () => {},
    disable = true,
    setViewEnvironmentalData = () => {},
    viewEnvironmentalData = false,
    saveIngredientImage = () => {},
    currentDataType = () => {},
    lcaRef,
    isEnableEnvironmentData,
    onChangeValidateData = () => {},
    checkCurrentSupplier,
    setSupplierValidation = () => {},
    setIsSupplierFormOpen = () => {}
  } = props;

  const [supplierData, setSupplierData] = useState<SupplierDataShape>({});
  const [supplierDocuments, setSupplierDocuments] = useState<any[]>([]);

  const ingredientId = getIngredientId(ingredientData);
  const supplierId = getSupplierId(supplierData);

  const supplierAsUser = useMemo(
    () =>
      Boolean(
        (ingredientData as Record<string, unknown>)?.supplierAsUser ||
          supplierData?.supplier_data?.is_supplier_as_user
      ),
    [ingredientData, supplierData]
  );

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  const countries = useCallback(() => {
    if (isCountriesDataSuccess) {
      return (countriesData as unknown as Array<{ id: number; country: string }>)?.map(c => ({
        key: c.id,
        text: c.country,
        value: c.id.toString()
      }));
    }
    return undefined;
  }, [countriesData, isCountriesDataSuccess]);

  const ingredientSuppliers = (
    (ingredientData as Record<string, unknown>)?.ingredient_data as
      | Record<string, unknown>
      | undefined
  )?.suppliers as Array<{ _id?: string }> | undefined;

  const {
    newSupplier,
    setNewSupplier,
    ingredientType,
    setIngredientType,
    defaultType,
    setDefaultType,
    country,
    setCountry,
    supplierCountry,
    setSupplierCountry,
    isSupplierAsUser,
    setIsSupplierAsUser,
    produceType,
    setProduceType,
    farmingType,
    setFarmingType,
    fieldType,
    setFieldType,
    specifyIngredientValue,
    setSpecifyIngredientValue,
    isSupplierExist,
    setIsSupplierExist,
    resetFields,
    applyIngredientDetails
  } = useSupplierFieldState({
    currentDataType,
    loadPackageData,
    getDefaultCountry: () => countries()?.[0]?.value
  });

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    control,
    errors,
    registerNewSupplier,
    resetNewSupplier,
    handleSubmitNewSupplier,
    controlNewSupplier,
    errorsNewSupplier
  } = useSupplierRegisterForm({
    newSupplier,
    country,
    specifyIngredientValue,
    produceType,
    ingredientType,
    setSupplierValidation,
    setIsSupplierFormOpen,
    onChangeValidateData
  });

  const resetSupplierFields = useCallback(() => {
    reset();
    resetFields();
  }, [reset, resetFields]);

  const { salesUnitData, isSalesUnitDataSuccess } = useSupplierSalesUnitSync({
    ingredientId,
    supplierId,
    isSupplierExist,
    setIsSupplierExist,
    ingredientSuppliers,
    selectedSupplierId: supplierData?.supplier_data?._id,
    applyIngredientDetails,
    setViewEnvironmentalData,
    setValue
  });

  const { effectiveDataType, setDataType } = useSupplierDataType({
    supplierData,
    setSupplierData,
    salesUnitData,
    supplierAsUser,
    currentDataType
  });

  const {
    closeSupplierData,
    saveValues,
    getSearchValue,
    handleSearchValidation
  } = useSupplierSelection({
    ingredientId,
    resetFields: resetSupplierFields,
    applyIngredientDetails,
    setValue,
    setViewEnvironmentalData,
    setNewSupplier,
    setIsSupplierFormOpen,
    setIsSupplierAsUser,
    hideIngredientView,
    resetNewSupplier,
    supplierCurrentData,
    setSupplierData,
    setSupplier,
    loadPackageData,
    checkCurrentSupplier
  });

  const { saveData, handleSupplierSave } = useSupplierSave({
    supplierData,
    ingredientData,
    effectiveDataType,
    viewEnvironmentalData,
    lcaRef,
    ingredientType,
    defaultType,
    country,
    specifyIngredientValue,
    produceType,
    farmingType,
    fieldType,
    supplierCountry,
    isSupplierAsUser,
    saveValues,
    refetchSupplier,
    setIngredientData,
    saveIngredientImage,
    setSupplierData,
    supplierCurrentData,
    checkCurrentSupplier,
    resetNewSupplier,
    setNewSupplier,
    setIsSupplierFormOpen,
    hideIngredientView,
    setIsSupplierAsUser
  });

  useImperativeHandle(ref, () => ({
    resetSupplierData() {
      closeSupplierData();
    },

    saveData: (documents: any[] = []) => {
      return new Promise<void>((resolve, reject) => {
        const onSuccess = () => resolve();
        const onError = (err: unknown) => reject(err);

        if (newSupplier) {
          handleSubmitNewSupplier((data: Record<string, unknown>) => {
            // Include supporting documents if available
            if (documents.length > 0) {
              data.supportingDocuments = documents;
            }
            handleSupplierSave(data);
            resolve();
          })();
        } else {
          handleSubmit((data: Record<string, unknown>) => {
            // Include supporting documents if available
            if (documents.length > 0) {
              data.supportingDocuments = documents;
            }
            saveData(data, onSuccess, onError);
          })();
        }
      });
    },

    setSupplierDocuments: (documents: any[]) => {
      console.log("Setting supplier documents:", documents.length);
      setSupplierDocuments(documents);
    },

    getSupplierData: () => supplierData
  }));

  const loadSupplierEditData = useCallback(() => {
    if (disableEdit) {
      setValue(
        "supplier_name",
        ((ingredientData as Record<string, unknown>)?.supplier_name as string) || ""
      );
      saveValues(
        ingredientData as Record<string, unknown>,
        (ingredientData as Record<string, unknown>)
          ?.supplierIngredientDetails as SupplierIngredientDetails
      );
    }
  }, [disableEdit, ingredientData, saveValues, setValue]);

  useEffect(() => {
    resetSupplierFields();
    loadSupplierEditData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredientData]);

  const {
    data: produceTypesData,
    isSuccess: isProduceTypesSuccess,
    isLoading: isProduceTypesLoading
  } = useGetProduceTypes();

  const produceTypes = useMemo(() => {
    if (isProduceTypesSuccess) {
      return (produceTypesData as unknown as Array<{ produce_name: string; id: number }>)?.map(
        p => ({
        text: p.produce_name,
        key: p.id,
        value: p.id
        })
      );
    }
    return undefined;
  }, [produceTypesData, isProduceTypesSuccess]);

  const { data: specifyIngredient } = useGetSpecifyIngredient();

  const packageFlags = useMemo(
    () => derivePackageFlags(effectiveDataType, ingredientType),
    [effectiveDataType, ingredientType]
  );

  useEffect(() => {
    changeDataIngredient(packageFlags);
  }, [changeDataIngredient, packageFlags]);

  if (!isSalesUnitDataSuccess && isSupplierExist) {
    return (
      <Dimmer active className="dimmerActive">
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      {newSupplier ? (
        <SupplierRegisterForm
          handleSubmit={handleSubmitNewSupplier}
          onSubmit={handleSupplierSave}
          register={registerNewSupplier}
          errors={errorsNewSupplier}
          setCountry={setSupplierCountry}
          country={supplierCountry}
          setIsSupplierAsUser={setIsSupplierAsUser}
          isSupplierAsUser={isSupplierAsUser}
        />
      ) : (
        <SupplerIngredientDataView
          dataView={dataView}
          supplierAsUser={supplierAsUser}
          register={register}
          control={control}
          ingredientType={ingredientType}
          setIngredientType={setIngredientType}
          dataType={effectiveDataType}
          errors={errors}
          country={country}
          countries={() => countries()}
          setCountry={setCountry}
          isCountriesDataLoading={isCountriesDataLoading}
          defaultType={defaultType}
          setDefaultType={setDefaultType}
          setDataType={setDataType}
          produceType={produceType}
          produceTypes={produceTypes}
          setProduceType={setProduceType}
          isProduceTypesLoading={isProduceTypesLoading}
          farmingType={farmingType}
          fieldType={fieldType}
          setFarmingType={setFarmingType}
          setFieldType={setFieldType}
          disableEdit={disableEdit}
          errorsNewSupplier={errorsNewSupplier}
          ingredientSupplierList={ingredientSupplierList}
          supplierData={supplierData}
          handleSearchValidation={handleSearchValidation}
          getSearchValue={getSearchValue}
          checkRawOrProcess={packageFlags}
          specifyIngredient={specifyIngredient}
          specifyIngredientValue={specifyIngredientValue}
          setSpecifyIngredientValue={setSpecifyIngredientValue}
          disable={disable}
          setViewEnvironmentalData={setViewEnvironmentalData}
          viewEnvironmentalData={viewEnvironmentalData}
          ingredientData={ingredientData}
          isEnableEnvironmentData={isEnableEnvironmentData}
        />
      )}
    </>
  );
});

SupplerRegister.displayName = "SupplerRegister";

export default SupplerRegister;
